/**
 * Links Management Page
 *
 * Displays all public links created by the user for content sharing.
 * Allows managing (viewing, copying, revoking, deleting) public links.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreIcon,
  Search as SearchIcon,
  BlockOutlined as RevokeIcon,
  Delete as DeleteIcon,
  OpenInNew as PreviewIcon,
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
  Lock as PasswordIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useOrganization } from '../context/OrganizationContext';
import { publicLinksService } from '../services/public-links';
import { ledeSx } from '../theme/styleHelpers';

interface PublicLink {
  linkId: string;
  itemId: string;
  itemType: 'content' | 'view';
  itemName: string; // Name of the content/view
  token: string;
  fullUrl: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
  accessCount: number;
  maxUses?: number;
  hasPassword: boolean;
  description?: string;
}

const Links: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { currentTeamId } = useOrganization();
  const [links, setLinks] = useState<PublicLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLink, setSelectedLink] = useState<PublicLink | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  useEffect(() => {
    if (currentTeamId) {
      fetchLinks();
    }
  }, [currentTeamId]);

  const fetchLinks = async () => {
    if (!currentTeamId) {
      setError('No team selected. Please select a team to view links.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch all public links for the current user in the selected team
      // SECURITY: teamId is required to enforce team boundaries
      const linksResponse = await publicLinksService.getMyLinks({
        teamId: currentTeamId,
        includeRevoked: true,
        includeExpired: true,
      });

      const fetchedLinks = linksResponse.links || [];

      // Convert to our PublicLink format
      const allLinks: PublicLink[] = fetchedLinks.map((link: any) => ({
        linkId: link.linkId,
        itemId: link.itemId,
        itemType: link.itemType,
        itemName: link.itemName || 'Unknown', // Backend should populate this
        token: link.token,
        fullUrl: link.fullUrl,
        createdAt: link.createdAt,
        expiresAt: link.expiresAt,
        isActive: link.isActive,
        accessCount: link.accessCount,
        maxUses: link.maxUses,
        hasPassword: link.hasPassword,
        description: link.description,
      }));

      setLinks(allLinks);
    } catch (err: any) {
      console.error('Failed to fetch links:', err);
      setError(err.response?.data?.error?.message || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, link: PublicLink) => {
    setAnchorEl(event.currentTarget);
    setSelectedLink(link);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLink(null);
  };

  const handleCopyLink = (link: PublicLink) => {
    navigator.clipboard.writeText(link.fullUrl);
    showNotification('Link copied to clipboard', 'success');
    handleMenuClose();
  };

  const handleShareLink = async (link: PublicLink) => {
    // Try to use native share API (works on mobile and modern browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: link.itemName,
          text: link.description || `Check out this ${link.itemType}`,
          url: link.fullUrl,
        });
        handleMenuClose();
      } catch (err) {
        // User cancelled or share failed
        if ((err as Error).name !== 'AbortError') {
          console.error('Share failed:', err);
          // Fallback to copy
          handleCopyLink(link);
        }
      }
    } else {
      // Fallback to copy for browsers without native share
      handleCopyLink(link);
    }
  };

  const handlePreview = (link: PublicLink) => {
    window.open(link.fullUrl, '_blank');
    handleMenuClose();
  };

  const handleRevokeConfirm = async () => {
    if (!selectedLink || !currentTeamId) return;

    try {
      await publicLinksService.revokeLink(selectedLink.linkId, currentTeamId);

      showNotification('Link revoked successfully', 'success');
      setRevokeDialogOpen(false);
      setSelectedLink(null);
      fetchLinks(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to revoke link:', err);
      showNotification(
        err.message || 'Failed to revoke link',
        'error'
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLink || !currentTeamId) return;

    try {
      await publicLinksService.deleteLink(selectedLink.linkId, currentTeamId);

      showNotification('Link deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSelectedLink(null);
      fetchLinks(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to delete link:', err);
      showNotification(
        err.message || 'Failed to delete link',
        'error'
      );
    }
  };

  const getStatusChip = (link: PublicLink) => {
    if (!link.isActive) {
      return <Chip label="Revoked" size="small" color="error" icon={<ExpiredIcon />} />;
    }
    if (link.expiresAt && new Date(link.expiresAt) < new Date()) {
      return <Chip label="Expired" size="small" color="warning" icon={<ExpiredIcon />} />;
    }
    if (link.maxUses && link.accessCount >= link.maxUses) {
      return <Chip label="Max Uses" size="small" color="warning" icon={<ExpiredIcon />} />;
    }
    return <Chip label="Active" size="small" color="success" icon={<ActiveIcon />} />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter links based on search query
  const filteredLinks = links.filter((link) =>
    link.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.linkId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Paginate filtered links
  const paginatedLinks = filteredLinks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" sx={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: '-0.025em',
          lineHeight: 1.15,
          marginBottom: 1,
        }}>
          Links
        </Typography>
        <Typography sx={ledeSx}>
          Manage and share content links from your team.
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search by content name or link ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {filteredLinks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LinkIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {searchQuery ? 'No links found' : 'No public links yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'Create public links from the content detail page to share your content'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Content/View</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="center">Usage</TableCell>
                  <TableCell align="center">Security</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLinks.map((link) => (
                  <TableRow key={link.linkId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {link.itemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {link.itemType === 'content' ? 'Content' : 'View'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={link.description ? 'text.primary' : 'text.secondary'}>
                        {link.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(link)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(link.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(link.expiresAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {link.accessCount}
                        {link.maxUses ? ` / ${link.maxUses}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {link.hasPassword && (
                        <Tooltip title="Password protected">
                          <PasswordIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Share link">
                        <IconButton size="small" onClick={() => handleShareLink(link)}>
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => handleCopyLink(link)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => handlePreview(link)}>
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, link)}>
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredLinks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedLink && handleShareLink(selectedLink)}>
          <ShareIcon fontSize="small" sx={{ mr: 1 }} />
          Share Link
        </MenuItem>
        <MenuItem onClick={() => selectedLink && handleCopyLink(selectedLink)}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy Link
        </MenuItem>
        <MenuItem onClick={() => selectedLink && handlePreview(selectedLink)}>
          <PreviewIcon fontSize="small" sx={{ mr: 1 }} />
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRevokeDialogOpen(true);
            setAnchorEl(null); // Close menu but keep selectedLink
          }}
          disabled={!selectedLink?.isActive}
        >
          <RevokeIcon fontSize="small" sx={{ mr: 1 }} />
          Revoke Link
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setAnchorEl(null); // Close menu but keep selectedLink
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Link
        </MenuItem>
      </Menu>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
        <DialogTitle>Revoke Public Link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately prevent access to this link. The link will still exist but will
            show as revoked. You can delete it permanently later if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRevokeConfirm} color="error" variant="contained">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Public Link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this public link and all its access history. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Links;
